import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  NavbarBackLink,
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
  Searchbar,
  Sheet,
} from 'konsta/react'
import { X } from 'lucide-react'
import { formatCurrency, formatRelativeTime } from '@/lib/utils'

type Filter = 'all' | 'won' | 'lost'

interface HandRecord {
  id: string
  room: string
  result: number
  cards: string
  board: string
  handType: string
  pot: number
  date: Date
}

const mockHands: HandRecord[] = [
  { id: 'H001', room: '新手桌 #1', result: 4520, cards: 'A♠ K♠', board: 'Q♠ J♠ 10♠ 3♥ 7♦', handType: '同花順', pot: 9040, date: new Date(Date.now() - 1000 * 60 * 30) },
  { id: 'H002', room: '中級桌 #3', result: -2000, cards: 'K♥ Q♥', board: 'A♣ 7♠ 2♦ 9♥ 4♣', handType: '高牌', pot: 4000, date: new Date(Date.now() - 1000 * 60 * 60 * 2) },
  { id: 'H003', room: '新手桌 #1', result: 1500, cards: 'A♦ A♣', board: 'A♥ 8♠ 3♦ K♠ 2♣', handType: '三條', pot: 3000, date: new Date(Date.now() - 1000 * 60 * 60 * 4) },
  { id: 'H004', room: '高額桌 VIP', result: -5000, cards: 'J♠ J♦', board: 'Q♥ Q♠ 3♣ 7♦ Q♦', handType: '葫蘆', pot: 10000, date: new Date(Date.now() - 1000 * 60 * 60 * 8) },
  { id: 'H005', room: '中級桌 #3', result: 3200, cards: '10♣ 10♠', board: '10♥ 4♦ 8♣ 2♠ A♦', handType: '三條', pot: 6400, date: new Date(Date.now() - 1000 * 60 * 60 * 24) },
  { id: 'H006', room: '新手桌 #2', result: -800, cards: '9♥ 8♥', board: 'K♠ 5♣ 2♦ J♥ 3♠', handType: '高牌', pot: 1600, date: new Date(Date.now() - 1000 * 60 * 60 * 36) },
  { id: 'H007', room: '高額桌 #1', result: 12000, cards: 'A♥ K♥', board: 'Q♥ J♥ 10♥ 5♣ 8♦', handType: '皇家同花順', pot: 24000, date: new Date(Date.now() - 1000 * 60 * 60 * 48) },
  { id: 'H008', room: '新手桌 #1', result: 600, cards: 'Q♣ Q♦', board: '7♠ 3♥ Q♠ 9♦ 2♣', handType: '三條', pot: 1200, date: new Date(Date.now() - 1000 * 60 * 60 * 60) },
]

const filterLabels: Record<Filter, string> = {
  all: '全部',
  won: '贏',
  lost: '輸',
}

export function HandHistoryScreen() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [selectedHand, setSelectedHand] = useState<HandRecord | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const filteredHands = useMemo(() => {
    let hands = mockHands

    if (filter === 'won') hands = hands.filter((h) => h.result > 0)
    if (filter === 'lost') hands = hands.filter((h) => h.result < 0)

    if (search.trim()) {
      const q = search.toLowerCase()
      hands = hands.filter(
        (h) =>
          h.id.toLowerCase().includes(q) ||
          h.room.toLowerCase().includes(q) ||
          h.handType.toLowerCase().includes(q) ||
          h.cards.toLowerCase().includes(q)
      )
    }

    return hands
  }, [filter, search])

  const openDetail = (hand: HandRecord) => {
    setSelectedHand(hand)
    setSheetOpen(true)
  }

  const closeDetail = () => {
    setSheetOpen(false)
    setSelectedHand(null)
  }

  return (
    <Page className="bg-[#F2F2F7]">
      <Navbar
        title="手牌歷史"
        left={<NavbarBackLink onClick={() => navigate(-1)} />}
      />

      <Block className="!mt-4 !mb-2">
        <Segmented strong rounded>
          {(Object.keys(filterLabels) as Filter[]).map((key) => (
            <SegmentedButton
              key={key}
              active={filter === key}
              onClick={() => setFilter(key)}
            >
              {filterLabels[key]}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      <Searchbar
        placeholder="搜尋手牌 ID、房間、牌型..."
        value={search}
        onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
      />

      {filteredHands.length === 0 ? (
        <Block className="text-center py-12">
          <p className="text-[#8E8E93] text-sm">找不到符合條件的手牌記錄</p>
        </Block>
      ) : (
        <>
          <BlockTitle>
            共 {filteredHands.length} 筆記錄
          </BlockTitle>
          <List strongIos insetIos>
            {filteredHands.map((hand) => (
              <ListItem
                key={hand.id}
                title={`${hand.id} - ${hand.handType}`}
                subtitle={formatRelativeTime(hand.date)}
                text={hand.room}
                after={
                  <span
                    className={`font-semibold ${
                      hand.result >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                    }`}
                  >
                    {hand.result >= 0 ? '+' : ''}{formatCurrency(hand.result)}
                  </span>
                }
                link
                onClick={() => openDetail(hand)}
              />
            ))}
          </List>
        </>
      )}

      <Sheet
        opened={sheetOpen}
        onBackdropClick={closeDetail}
        className="pb-safe"
      >
        {selectedHand && (
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-black">
                手牌詳情 - {selectedHand.id}
              </h3>
              <button
                onClick={closeDetail}
                className="w-8 h-8 rounded-full bg-[#F2F2F7] flex items-center justify-center"
              >
                <X className="w-4 h-4 text-[#8E8E93]" />
              </button>
            </div>

            {/* Cards Section */}
            <div className="mb-4">
              <p className="text-xs text-[#8E8E93] mb-1 font-medium">手牌</p>
              <div className="flex gap-2">
                {selectedHand.cards.split(' ').map((card, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center justify-center w-12 h-16 rounded-lg border-2 font-bold text-lg ${
                      card.includes('♥') || card.includes('♦')
                        ? 'border-red-200 bg-red-50 text-[#FF3B30]'
                        : 'border-gray-200 bg-gray-50 text-black'
                    }`}
                  >
                    {card}
                  </span>
                ))}
              </div>
            </div>

            {/* Board Section */}
            <div className="mb-4">
              <p className="text-xs text-[#8E8E93] mb-1 font-medium">公牌</p>
              <div className="flex gap-1.5 flex-wrap">
                {selectedHand.board.split(' ').map((card, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center justify-center w-11 h-14 rounded-lg border font-bold text-base ${
                      card.includes('♥') || card.includes('♦')
                        ? 'border-red-200 bg-red-50 text-[#FF3B30]'
                        : 'border-gray-200 bg-gray-50 text-black'
                    }`}
                  >
                    {card}
                  </span>
                ))}
              </div>
            </div>

            {/* Details */}
            <div className="bg-[#F2F2F7] rounded-xl p-4 space-y-3">
              <DetailRow label="房間" value={selectedHand.room} />
              <DetailRow label="牌型" value={selectedHand.handType} />
              <DetailRow label="底池" value={formatCurrency(selectedHand.pot)} />
              <DetailRow
                label="結果"
                value={`${selectedHand.result >= 0 ? '+' : ''}${formatCurrency(selectedHand.result)}`}
                valueColor={selectedHand.result >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}
              />
              <DetailRow label="時間" value={formatRelativeTime(selectedHand.date)} />
            </div>
          </div>
        )}
      </Sheet>
    </Page>
  )
}

function DetailRow({
  label,
  value,
  valueColor = 'text-black',
}: {
  label: string
  value: string
  valueColor?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#8E8E93]">{label}</span>
      <span className={`text-sm font-semibold ${valueColor}`}>{value}</span>
    </div>
  )
}
